import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';

import styles from './ManualInputField.module.scss';

import { HttpService, IHttpService } from '../../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../../services/notifierService';
import { ProductDetailsMarketShareListingResponseModel } from '../../../../models/factMarketShareDatas/ProductDetailsMarketShareListingResponseModel';

export interface IManualInputFieldProps {
	dataCompetitorId?: string;
	userCanEdit: boolean;
	year: number;
	quarter: number;
	units: number | string;
	dataInputTypeId: number;
	divisionId: number | string;
	brandId?: number | string | null;
	countryId: string | number;
	productId: number;
	fetchProducts: (
		updateTable: boolean,
		silentUpdate: boolean
	) => Promise<ProductDetailsMarketShareListingResponseModel[]>;
}

const ManualInputField = ({
	dataCompetitorId,
	userCanEdit,
	year,
	quarter,
	units,
	dataInputTypeId,
	divisionId,
	brandId,
	countryId,
	productId,
	fetchProducts
}: IManualInputFieldProps): JSX.Element => {
	const [cellValue, setCellValue] = useState<string>(units.toString());
	const [lastCellValue, setLastCellValue] = useState<string>(
		units.toString()
	);
	const [cellUpdated, setCellUpdated] = useState<boolean>(false);
	const [inputLoading, setInputLoading] = useState<boolean>(false);

	useEffect(() => {
		setCellValue(units.toString());
		setLastCellValue(units.toString());
	}, [fetchProducts]);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const handleValueChange = (event: React.FormEvent<HTMLInputElement>) => {
		let newValue = event.currentTarget.value
			.replace(/\D/g, '')
			.replace(/^(?:0+(?=[1-9])|0+(?=0$))/gm, '');

		if (newValue.length === 0) {
			newValue = '';
		}

		setCellValue(newValue);
	};

	const handleKeyboardCommands = (
		event: React.KeyboardEvent<HTMLInputElement>
	) => {
		if (event.key === 'Enter') {
			event.currentTarget.blur();
		}
	};

	const handleBlur = () => {
		if (
			cellValue.length === 0 ||
			cellValue === '-' ||
			cellValue === '-0' ||
			/^-0\d*$/.test(cellValue)
		) {
			setCellValue('0');
		}

		updateProductInDb();
	};

	const updateProductInDb = () => {
		const inputUnitsInt = isNaN(parseInt(cellValue))
			? 0
			: parseInt(cellValue);

		const newProductDataJson = JSON.stringify({
			year: year,
			quarter: quarter,
			units: inputUnitsInt,
			dataInputTypeId: dataInputTypeId,
			divisionId: divisionId,
			brandId: brandId,
			countryId: countryId,
			productDetailIds: productId
		});

		if (lastCellValue !== cellValue) {
			setInputLoading(true);

			httpService
				.post(
					'factMarketShareDatas',
					newProductDataJson,
					undefined,
					false
				)
				.then(() => {
					updateRowData();
					setCellUpdated(true);
					setLastCellValue(cellValue);
				})
				.catch((err) => {
					notifierService.error();
					setCellValue(lastCellValue);
					setCellUpdated(false);
				})
				.finally(() => {
					setInputLoading(false);
				});
		}
	};

	const updateRowData = useCallback(async () => {
		fetchProducts(true, true);
	}, [fetchProducts]);

	return (
		<>
			<div
				className={`${styles.manualInputField} ${
					cellUpdated ? styles.cellUpdated : ''
				} ${userCanEdit ? '' : styles.static}`}
			>
				{userCanEdit ? (
					<input
						data-competitor-id={dataCompetitorId}
						type='string'
						placeholder='0'
						value={cellValue}
						onChange={handleValueChange}
						onKeyDown={handleKeyboardCommands}
						onBlur={handleBlur}
						className={inputLoading ? styles.loading : ''}
					/>
				) : (
					<p>{cellValue == '' ? 0 : cellValue}</p>
				)}
			</div>
		</>
	);
};

export default ManualInputField;
